import { inferRouterOutputs } from '@trpc/server'
import { AppRouter } from '@tagadapay/trpc-types'

import { trpc } from './trpc'

/**
 * Merchant billing-method hooks: the card on file for the account's own
 * CRM fees, on whichever provider bills the account (Stripe for legacy
 * accounts, Checkout.com for new ones). Successors of the `stripe.*` card
 * hooks in `./stripe.ts`.
 */

type OutputRouter = inferRouterOutputs<AppRouter>

export type BillingPaymentMethodStatus = OutputRouter['billing']['getPaymentMethodStatus']
export type BillingSetupSession = OutputRouter['billing']['createSetupSession']
export type BillingPaymentMethod = OutputRouter['billing']['listPaymentMethods']['methods'][number]
export type BillingProviderSlug = BillingPaymentMethodStatus['provider']

export const useBillingPaymentMethods = () => {
	return trpc.billing.listPaymentMethods.useQuery({})
}

/** Starts a card setup; the result is tagged by provider so the UI picks the matching form. */
export const useCreateBillingSetupSession = () => {
	return trpc.billing.createSetupSession.useMutation()
}

/**
 * Completes a card setup after the provider-side confirmation (Stripe:
 * confirmed payment method id; Checkout.com: Frames token). Invalidates the
 * gate and the card list so the CRM unblocks without a reload.
 */
export const useCompleteBillingSetup = () => {
	const utils = trpc.useUtils()
	return trpc.billing.completeSetup.useMutation({
		onSuccess: () => {
			utils.billing.getPaymentMethodStatus.invalidate()
			utils.billing.listPaymentMethods.invalidate()
		},
	})
}

export const useSetDefaultBillingPaymentMethod = () => {
	const utils = trpc.useUtils()
	return trpc.billing.setDefaultPaymentMethod.useMutation({
		onSuccess: () => {
			utils.billing.getPaymentMethodStatus.invalidate()
			utils.billing.listPaymentMethods.invalidate()
		},
	})
}

/** Provider-hosted card management page (Stripe Customer Portal); `url` is null when there is none. */
export const useBillingManagementUrl = () => {
	return trpc.billing.getManagementUrl.useMutation()
}
